import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { paymentAPI, paypalAPI } from '../services/api';
import PayPalButton from '../components/PayPalButton';
import './Pricing.css';

const PLANS = [
  {
    key: 'trial',
    name: 'Free Trial',
    description: '14 days to explore everything',
    monthly: { INR: 0 },
    annual:  { INR: 0 },
    features: [
      { label: 'Participants per meeting', value: '20' },
      { label: 'Meeting duration', value: '40 min' },
      { label: 'Recordings stored', value: '5' },
      { label: 'Speakers in transcript', value: '20' },
      { label: 'AI Transcription', value: true },
      { label: 'Download recordings', value: true },
      { label: 'Google Sign-In', value: true },
      { label: 'Blockchain certificate', value: false },
      { label: 'Priority support', value: false },
    ],
  },
  {
    key: 'basic',
    name: 'Basic',
    badge: null,
    description: 'For individuals & small teams',
    monthly: { INR: 499, USD: 6 },
    annual:  { INR: 399, USD: 5 },
    features: [
      { label: 'Participants per meeting', value: '20' },
      { label: 'Meeting duration', value: '90 min' },
      { label: 'Recordings stored', value: '25' },
      { label: 'Speakers in transcript', value: '20' },
      { label: 'AI Transcription', value: true },
      { label: 'Download recordings', value: true },
      { label: 'Google Sign-In', value: true },
      { label: 'Blockchain certificate', value: false },
      { label: 'Priority support', value: false },
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    badge: 'Most Popular',
    description: 'For growing teams & enterprises',
    monthly: { INR: 1299, USD: 15 },
    annual:  { INR: 999,  USD: 12 },
    features: [
      { label: 'Participants per meeting', value: '100' },
      { label: 'Meeting duration', value: 'Unlimited' },
      { label: 'Recordings stored', value: 'Unlimited' },
      { label: 'Speakers in transcript', value: '50' },
      { label: 'AI Transcription', value: true },
      { label: 'Download recordings', value: true },
      { label: 'Google Sign-In', value: true },
      { label: 'Blockchain certificate', value: true },
      { label: 'Priority support', value: true },
    ],
  },
];

function loadCashfreeScript() {
  return new Promise((resolve) => {
    if (window.Cashfree) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function detectCurrency() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === 'Asia/Calcutta' || tz === 'Asia/Kolkata') return 'INR';
    const locale = navigator.language || '';
    if (locale.endsWith('-IN') || locale === 'hi') return 'INR';
  } catch {}
  return 'USD';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Phone input modal shown before checkout
function PhoneModal({ planKey, billing, onConfirm, onCancel }) {
  const [phone, setPhone] = useState('');
  const [err, setErr] = useState('');

  const submit = () => {
    if (!/^[6-9]\d{9}$/.test(phone)) {
      setErr('Enter a valid 10-digit Indian mobile number');
      return;
    }
    onConfirm(phone);
  };

  const planLabel = planKey.charAt(0).toUpperCase() + planKey.slice(1);
  const prices = { basic: { monthly: 499, annual: 399 }, pro: { monthly: 1299, annual: 999 } };
  const amount = prices[planKey]?.[billing];

  return (
    <div className="phone-modal-overlay" onClick={onCancel}>
      <div className="phone-modal" onClick={e => e.stopPropagation()}>
        <h3 className="phone-modal__title">Confirm Payment</h3>
        <p className="phone-modal__plan">
          {planLabel} · {billing === 'annual' ? 'Annual' : 'Monthly'} — <strong>₹{amount}/mo</strong>
        </p>
        <p className="phone-modal__desc">Enter your mobile number for payment verification</p>
        <div className="phone-modal__field">
          <span className="phone-modal__prefix">+91</span>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            placeholder="9876543210"
            value={phone}
            onChange={e => { setPhone(e.target.value.replace(/\D/g, '')); setErr(''); }}
            onKeyDown={e => e.key === 'Enter' && submit()}
            autoFocus
            className="phone-modal__input"
          />
        </div>
        {err && <p className="phone-modal__error">{err}</p>}
        <div className="phone-modal__actions">
          <button className="phone-modal__cancel" onClick={onCancel}>Cancel</button>
          <button className="phone-modal__pay" onClick={submit}>
            Proceed to Pay ₹{billing === 'annual' ? amount * 12 : amount}
          </button>
        </div>
        <p className="phone-modal__secure">
          🔒 Secured by Cashfree · UPI · Cards · Net Banking
        </p>
        <p className="phone-modal__upi-tip">
          💡 Paying via UPI / Google Pay? After approval, return to this tab — your plan activates automatically.
        </p>
      </div>
    </div>
  );
}

export default function Pricing() {
  const [billing, setBilling] = useState('monthly');
  const [currency, setCurrency] = useState(detectCurrency);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [phoneModal, setPhoneModal] = useState(null);    // { planKey, billing } — INR/Cashfree
  const [paypalModal, setPaypalModal] = useState(null);  // { planKey, billing } — USD/PayPal
  const [paypalDone, setPaypalDone] = useState(false);

  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const fetchSubscription = useCallback(async () => {
    if (!user) return;
    try {
      const res = await paymentAPI.getSubscription();
      setSubscription(res.data.subscription);
    } catch {
      // non-fatal
    }
  }, [user]);

  useEffect(() => { fetchSubscription(); }, [fetchSubscription]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 7000);
  };

  const handleSubscribeClick = (planKey) => {
    if (!user) { navigate('/login'); return; }
    if (loadingPlan) return;
    if (currency === 'USD') {
      setPaypalDone(false);
      setPaypalModal({ planKey, billing });
    } else {
      setPhoneModal({ planKey, billing });
    }
  };

  const handlePayPalCreate = async () => {
    const res = await paypalAPI.createPlanOrder(paypalModal.planKey, paypalModal.billing);
    return res.data.orderID;
  };

  const handlePayPalApprove = async ({ orderID }) => {
    try {
      const res = await paypalAPI.capturePlanOrder(orderID);
      await refreshUser();
      setPaypalDone(true);
      const planLabel = res.data.plan.charAt(0).toUpperCase() + res.data.plan.slice(1);
      toast.success(`Welcome to ${planLabel} plan! Payment confirmed.`);
      setTimeout(() => setPaypalModal(null), 3000);
      await fetchSubscription();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Payment capture failed. Contact support.');
      setPaypalModal(null);
    }
  };

  const handlePhoneConfirm = async (phone) => {
    const { planKey } = phoneModal;
    setPhoneModal(null);
    setLoadingPlan(planKey);
    setMessage(null);

    try {
      const scriptLoaded = await loadCashfreeScript();
      if (!scriptLoaded) {
        showMessage('error', 'Failed to load payment gateway. Please check your connection.');
        setLoadingPlan(null);
        return;
      }

      const res = await paymentAPI.createOrder(planKey, billing, phone);
      const { payment_session_id, cf_env } = res.data;

      const cashfree = window.Cashfree({ mode: cf_env === 'production' ? 'production' : 'sandbox' });
      cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: '_self',
      });
      // Page will redirect — no setLoadingPlan(null) needed here
    } catch (err) {
      const msg = err.response?.data?.error || 'Something went wrong. Please try again.';
      showMessage('error', msg);
      setLoadingPlan(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Your plan will not be renewed. You retain access until your current period ends.')) return;
    setCancelLoading(true);
    try {
      const res = await paymentAPI.cancelSubscription();
      showMessage('success', res.data.message);
      await fetchSubscription();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to process cancellation.');
    } finally {
      setCancelLoading(false);
    }
  };

  const formatPrice = (plan) => {
    if (currency === 'USD') {
      const price = plan[billing]?.USD;
      if (!price) return 'Free';
      return `$${price}`;
    }
    const price = plan[billing]?.INR;
    if (!price) return 'Free';
    return `₹${price}`;
  };

  const annualSavingsPct = (plan) => {
    if (currency === 'USD') {
      const m = plan.monthly?.USD;
      const a = plan.annual?.USD;
      if (!m || !a) return null;
      return Math.round(((m - a) / m) * 100);
    }
    const m = plan.monthly?.INR;
    const a = plan.annual?.INR;
    if (!m || !a) return null;
    return Math.round(((m - a) / m) * 100);
  };

  const getEffectivePlan = () => {
    if (!user) return null;
    if (user.plan !== 'trial' && user.plan_expires_at && new Date(user.plan_expires_at) < new Date()) return 'trial';
    return user.plan;
  };

  const effectivePlan = getEffectivePlan();
  const isActiveSub = subscription && subscription.status === 'PAID';

  const getCtaState = (planKey) => {
    if (!user) return { label: 'Get Started', disabled: false };
    if (planKey === 'trial') return { label: effectivePlan === 'trial' ? 'Current Plan' : 'Free Tier', disabled: true };
    if (planKey === effectivePlan && isActiveSub) return { label: 'Current Plan', disabled: true };
    return {
      label: loadingPlan === planKey ? 'Redirecting...' : `Upgrade to ${planKey.charAt(0).toUpperCase() + planKey.slice(1)}`,
      disabled: !!loadingPlan,
    };
  };

  return (
    <div className="pricing-page">

      {phoneModal && (
        <PhoneModal
          planKey={phoneModal.planKey}
          billing={phoneModal.billing}
          onConfirm={handlePhoneConfirm}
          onCancel={() => setPhoneModal(null)}
        />
      )}

      {paypalModal && (() => {
        const { planKey, billing: b } = paypalModal;
        const prices = { basic: { monthly: 6, annual: 60 }, pro: { monthly: 15, annual: 144 } };
        const amount = prices[planKey]?.[b === 'annual' ? 'annual' : 'monthly'];
        const planLabel = planKey.charAt(0).toUpperCase() + planKey.slice(1);
        return (
          <div className="phone-modal-overlay" onClick={() => !paypalDone && setPaypalModal(null)}>
            <div className="phone-modal paypal-modal" onClick={e => e.stopPropagation()}>
              <h3 className="phone-modal__title">Pay with PayPal</h3>
              <p className="phone-modal__plan">
                {planLabel} · {b === 'annual' ? 'Annual' : 'Monthly'} — <strong>${amount}</strong>
              </p>
              {paypalDone ? (
                <div className="paypal-modal__success">
                  <div className="paypal-modal__check">✓</div>
                  <p>Payment confirmed! Plan activated.</p>
                </div>
              ) : (
                <>
                  <PayPalButton
                    createOrder={handlePayPalCreate}
                    onApprove={handlePayPalApprove}
                    onError={() => showMessage('error', 'PayPal encountered an error. Please try again.')}
                  />
                  <button className="phone-modal__cancel" style={{ marginTop: '1rem' }} onClick={() => setPaypalModal(null)}>
                    Cancel
                  </button>
                </>
              )}
              <p className="phone-modal__secure">🔒 Secured by PayPal · No card stored on our servers</p>
            </div>
          </div>
        );
      })()}

      {message && (
        <div className={`pricing-message pricing-message--${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="pricing-header">
        <h1 className="pricing-title">Simple, transparent pricing</h1>
        <p className="pricing-subtitle">Start free. Upgrade when you're ready. No hidden fees.</p>

        <div className="pricing-toggles">
          <div className="toggle-group">
            <span className="toggle-group__label">Billing</span>
            <div className="billing-toggle">
              <button className={billing === 'monthly' ? 'active' : ''} onClick={() => setBilling('monthly')}>
                Monthly
              </button>
              <button className={billing === 'annual' ? 'active' : ''} onClick={() => setBilling('annual')}>
                Annual <span className="save-badge">Save 20%</span>
              </button>
            </div>
          </div>

          <div className="toggle-group">
            <span className="toggle-group__label">Currency</span>
            <div className="billing-toggle currency-toggle">
              <button className={currency === 'INR' ? 'active' : ''} onClick={() => setCurrency('INR')}>
                ₹ INR
              </button>
              <button className={currency === 'USD' ? 'active' : ''} onClick={() => setCurrency('USD')}>
                $ USD
              </button>
            </div>
          </div>
        </div>

        {billing === 'annual' && <p className="annual-note">Billed annually · Cancel anytime</p>}
        {currency === 'INR'
          ? <p className="upi-note">🇮🇳 UPI · Cards · Net Banking · Wallets powered by Cashfree</p>
          : <p className="upi-note">🌍 International payments via PayPal · Visa · Mastercard · Amex</p>
        }
      </div>

      <div className="pricing-cards">
        {PLANS.map((plan) => {
          const isCurrentPlan = effectivePlan === plan.key;
          const savings = annualSavingsPct(plan);
          const cta = getCtaState(plan.key);

          return (
            <div
              key={plan.key}
              className={[
                'pricing-card',
                plan.key === 'pro' ? 'pricing-card--popular' : '',
                isCurrentPlan ? 'pricing-card--current' : '',
              ].join(' ').trim()}
            >
              {plan.badge && <div className="pricing-card__badge">{plan.badge}</div>}
              {isCurrentPlan && <div className="pricing-card__current-badge">Your Plan</div>}

              <div className="pricing-card__header">
                <h2 className="pricing-card__name">{plan.name}</h2>
                <p className="pricing-card__desc">{plan.description}</p>
              </div>

              <div className="pricing-card__price">
                <span className="price-amount">{formatPrice(plan)}</span>
                {(plan.monthly?.INR > 0 || plan.monthly?.USD > 0) && <span className="price-period">/ month</span>}
                {billing === 'annual' && savings && (plan.monthly?.INR > 0 || plan.monthly?.USD > 0) && (
                  <span className="price-savings">Save {savings}%</span>
                )}
              </div>

              {plan.key !== 'trial' && (
                <button
                  className={[
                    'pricing-card__cta',
                    plan.key === 'pro' ? 'cta--pro' : '',
                    isCurrentPlan && isActiveSub ? 'cta--current' : '',
                    loadingPlan === plan.key ? 'cta--loading' : '',
                  ].join(' ').trim()}
                  onClick={() => !cta.disabled && handleSubscribeClick(plan.key)}
                  disabled={cta.disabled}
                >
                  {loadingPlan === plan.key ? <span className="cta-spinner" /> : cta.label}
                </button>
              )}

              {plan.key === 'trial' && (
                <div className="pricing-card__cta cta--current" style={{ textAlign: 'center', cursor: 'default' }}>
                  Free — No card required
                </div>
              )}

              <ul className="pricing-card__features">
                {plan.features.map((f, i) => (
                  <li key={i} className={`feature-item ${f.value === false ? 'feature-item--na' : ''}`}>
                    <span className="feature-icon">
                      {f.value === true ? '✓' : f.value === false ? '✕' : ''}
                    </span>
                    <span className="feature-label">{f.label}</span>
                    {typeof f.value === 'string' && <span className="feature-value">{f.value}</span>}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {user && subscription && (
        <div className="subscription-panel">
          <h3 className="subscription-panel__title">Your Subscription</h3>
          <div className="subscription-panel__details">
            <span className="sub-plan">
              {subscription.plan?.charAt(0).toUpperCase() + subscription.plan?.slice(1)}
              {' · '}{subscription.billing_cycle}
            </span>
            <span className={`sub-status sub-status--${subscription.status?.toLowerCase()}`}>
              {subscription.status === 'PAID' ? 'Active' : subscription.status}
            </span>
          </div>
          {isActiveSub && (
            <button
              className="subscription-panel__cancel"
              onClick={handleCancel}
              disabled={cancelLoading}
            >
              {cancelLoading ? 'Processing...' : 'Cancel Renewal'}
            </button>
          )}
        </div>
      )}

      <div className="pricing-footer">
        <p>All plans include end-to-end encrypted meetings · Cancel anytime</p>
        <p className="pricing-footer__upi">
          🇮🇳 Indian payments via UPI, Cards & Net Banking — Cashfree &nbsp;·&nbsp; 🌍 International via PayPal
        </p>
        <p className="pricing-footer__consult">
          Need a custom plan or enterprise pricing?{' '}
          <a href="/consultation" className="pricing-footer__link">Book a consultation →</a>
        </p>
      </div>
    </div>
  );
}
